/**
 * BuilderApiClientImpl
 *
 * HTTP client implementation for Builder Service API.
 * Sends Web Publishing requests to upload JSON configurations to GCS.
 *
 * @module placements/services
 */

import type {
  BuilderApiClient,
  DeleteABRoutingConfigParams,
  PublishWebHealthcheckParams,
  PublishWebHealthcheckResult,
  PublishWebParams,
  PublishWebResult,
  UploadABRoutingConfigParams,
  UploadABRoutingConfigResult,
} from './BuilderApiClient';
import { PublishWebError } from './BuilderApiClient';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

/**
 * HTTP client for Builder Service API
 *
 * Makes POST requests to the Builder Service to publish web placement
 * configurations to GCS.
 */
export class BuilderApiClientImpl implements BuilderApiClient {
  constructor(private readonly baseUrl: string) {}

  /** The signed-in user's Firebase ID token, or null (never throws, never leaks the auth error). */
  private async getIdTokenOrNull(): Promise<string | null> {
    try { return (await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken()) ?? null; }
    catch { return null; }
  }

  /**
   * Publish a web funnel configuration to GCS
   *
   * Uploads the FlowDocument to GCS at web-v2/{projectId}/{alias}/config.json
   * for serving via Edge Gateway at /{alias}
   *
   * @param params - Web publish parameters
   * @returns Web publish result with GCS path and URL
   * @throws Error if request fails
   */
  async publishWeb(params: PublishWebParams): Promise<PublishWebResult> {
    const url = `${this.baseUrl}/api/publish/web`;
    const body = JSON.stringify({
      alias: params.alias,
      funnelId: params.funnelId,
      versionId: params.versionId,
      config: params.config,
      projectId: params.projectId,
      // WebPlacement config for baking (parameters, SEO, analytics)
      webPlacementConfig: params.webPlacementConfig,
      // Project-level analytics settings (Amplitude, GA4, GTM, etc.)
      projectAnalytics: params.projectAnalytics,
    });
    let token: string | undefined;
    try { token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken(); }
    catch { /* Use the same recoverable, secret-free auth error below. */ }
    if (!token) throw new PublishWebError('Sign in before publishing.', 401, 'unauthenticated');

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorBody: { error?: string; code?: string; healthcheck?: { issues?: unknown[] } } = {};
      try {
        errorBody = JSON.parse(errorText) as typeof errorBody;
      } catch {
        // Non-JSON body — keep the bounded plain-text response as the message.
      }
      throw new PublishWebError(
        errorBody.error || `Builder Service error: ${response.status} ${response.statusText} - ${errorText}`,
        response.status,
        errorBody.code,
        errorBody.healthcheck?.issues,
      );
    }

    const result = await response.json();
    return result as PublishWebResult;
  }

  /**
   * Evaluate the publish privacy gate without publishing.
   *
   * The endpoint answers with the same structured body on 200/422/503, so the
   * body is returned for ANY status that parses as JSON; only network errors
   * and non-JSON responses throw.
   */
  async publishWebHealthcheck(params: PublishWebHealthcheckParams): Promise<PublishWebHealthcheckResult> {
    const url = `${this.baseUrl}/api/publish/web/healthcheck`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        config: params.config,
        projectId: params.projectId,
        projectAnalytics: params.projectAnalytics,
      }),
    });

    const bodyText = await response.text();
    try {
      return JSON.parse(bodyText) as PublishWebHealthcheckResult;
    } catch {
      throw new Error(
        `Builder Service healthcheck error: ${response.status} ${response.statusText} - ${bodyText}`,
      );
    }
  }

  /**
   * Upload A/B test routing config to GCS
   *
   * Uploads a lightweight JSON config (~200 bytes) to web-ab/{alias}.json
   * that the Edge Gateway uses to resolve variants.
   *
   * @param params - Upload parameters
   * @returns Upload result with GCS path
   * @throws Error if request fails
   */
  async uploadABRoutingConfig(params: UploadABRoutingConfigParams): Promise<UploadABRoutingConfigResult> {
    const url = `${this.baseUrl}/api/publish/web-ab`;
    const body = JSON.stringify({
      alias: params.alias,
      routingConfig: params.routingConfig,
      projectId: params.projectId,
      expectedRoutingHash: params.expectedRoutingHash,
    });
    const token = await this.getIdTokenOrNull();
    if (!token) return { success: false, error: 'Sign in before publishing.', errorCode: 'unauthenticated' };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorBody: { error?: string; code?: string } = { error: errorText };
        try {
          errorBody = JSON.parse(errorText) as { error?: string; code?: string };
        } catch {
          // Keep the bounded plain-text response from the Builder service.
        }
        return {
          success: false,
          error: `Builder Service error: ${response.status} ${response.statusText} - ${errorBody.error || 'request failed'}`,
          ...(errorBody.code ? { errorCode: errorBody.code } : {}),
        };
      }

      const result = await response.json();
      return {
        success: true,
        gcsPath: result.gcsPath || `web-ab/${params.alias}.json`,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async deleteABRoutingConfig(params: DeleteABRoutingConfigParams): Promise<UploadABRoutingConfigResult> {
    const url = `${this.baseUrl}/api/publish/web-ab`;
    const body = JSON.stringify({
      alias: params.alias,
      projectId: params.projectId,
      operation: 'delete',
      expectedRoutingHash: params.expectedRoutingHash,
    });
    const token = await this.getIdTokenOrNull();
    if (!token) return { success: false, error: 'Sign in before publishing.', errorCode: 'unauthenticated' };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorBody: { error?: string; code?: string } = { error: errorText };
        try {
          errorBody = JSON.parse(errorText) as { error?: string; code?: string };
        } catch {
          // Keep the bounded plain-text response from the Builder service.
        }
        return {
          success: false,
          error: `Builder Service error: ${response.status} ${response.statusText} - ${errorBody.error || 'request failed'}`,
          ...(errorBody.code ? { errorCode: errorBody.code } : {}),
        };
      }

      const result = await response.json();
      return {
        success: true,
        gcsPath: result.gcsPath,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
